import React, { useState, useEffect } from 'react';
import HeaderOne from "./HeaderTwo";
import BottomFooter from "./BottomFooter";

const TermsAndConditions = () => {
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      console.log('Fetching terms from API...');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/terms/`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch terms: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Fetched terms:', data);
      
      setTerms(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      console.error('Error fetching terms:', err);
    }
  };

  // Helper function to render list items
  const renderList = (listString) => {
    if (!listString) return null;
    return listString.split('\n').map((item, index) => {
      const trimmedItem = item.trim();
      return trimmedItem ? <li key={index}>{trimmedItem}</li> : null;
    });
  };

  if (loading) {
    return (
      <>
        <HeaderOne />
        <div className="terms-container">
          <div className="loading-spinner">
            <p>Loading Terms and Conditions...</p>
          </div>
        </div>
        <BottomFooter />
      </>
    );
  }

  if (error) {
    return (
      <>
        <HeaderOne />
        <div className="terms-container">
          <div className="error-message">
            <h2>Error</h2>
            <p style={{ color: 'red' }}>Error loading terms: {error}</p>
          </div>
        </div>
        <BottomFooter />
      </>
    );
  }

  if (!terms) {
    return (
      <>
        <HeaderOne />
        <div className="terms-container">
          <div className="error-message">
            <p>No terms and conditions available at the moment.</p>
          </div>
        </div>
        <BottomFooter />
      </>
    );
  }

  return (
    <>
      <HeaderOne />
      
      <div className="terms-container">
        <div className="terms-wrapper">
          <div className="terms-header">
            <h1>{terms.page_title}</h1>
            <p className="last-updated">Last Updated: {terms.last_updated}</p>
          </div>

          <div className="terms-content">
            {/* 1. Introduction */}
            <section className="terms-section">
              <h2>{terms.intro_title}</h2>
              <p>{terms.intro_text}</p>
            </section>

            {/* 2. Acceptance of Terms */}
            <section className="terms-section">
              <h2>{terms.acceptance_title}</h2>
              <p>{terms.acceptance_text}</p>
            </section>

            {/* 3. User Accounts */}
            <section className="terms-section">
              <h2>{terms.accounts_title}</h2>
              <div className="subsection">
                <h3>{terms.accounts_sub1_title}</h3>
                <p>{terms.accounts_sub1_text}</p>
              </div>
              <div className="subsection">
                <h3>{terms.accounts_sub2_title}</h3>
                <p>{terms.accounts_sub2_text}</p>
              </div>
            </section>

            {/* 4. User Responsibilities */}
            <section className="terms-section">
              <h2>{terms.responsibilities_title}</h2>
              <p>{terms.responsibilities_intro}</p>
              {terms.responsibilities_list && (
                <ul>{renderList(terms.responsibilities_list)}</ul>
              )}
            </section>

            {/* 5. Prohibited Activities */}
            <section className="terms-section">
              <h2>{terms.prohibited_title}</h2>
              <p>{terms.prohibited_intro}</p>
              {terms.prohibited_list && (
                <ul>{renderList(terms.prohibited_list)}</ul>
              )}
            </section>

            {/* 6. Intellectual Property */}
            <section className="terms-section">
              <h2>{terms.ip_title}</h2>
              <p>{terms.ip_text1}</p>
              {terms.ip_text2 && <p>{terms.ip_text2}</p>}
            </section>

            {/* 7. User Generated Content */}
            <section className="terms-section">
              <h2>{terms.ugc_title}</h2>
              <div className="subsection">
                <h3>{terms.ugc_sub1_title}</h3>
                <p>{terms.ugc_sub1_text}</p>
              </div>
              <div className="subsection">
                <h3>{terms.ugc_sub2_title}</h3>
                <p>{terms.ugc_sub2_text}</p>
              </div>
            </section>

            {/* 8. Third-Party Links */}
            <section className="terms-section">
              <h2>{terms.third_party_title}</h2>
              <p>{terms.third_party_text}</p>
            </section>

            {/* 9. Payment Terms */}
            <section className="terms-section">
              <h2>{terms.payment_title}</h2>
              <p>{terms.payment_text1}</p>
              {terms.payment_text2 && <p>{terms.payment_text2}</p>}
            </section>

            {/* 10. Refunds */}
            <section className="terms-section">
              <h2>{terms.refund_title}</h2>
              <p>{terms.refund_text}</p>
            </section>

            {/* 11. Limitation of Liability */}
            <section className="terms-section">
              <h2>{terms.liability_title}</h2>
              <p>{terms.liability_text}</p>
              {terms.liability_list && (
                <ul>{renderList(terms.liability_list)}</ul>
              )}
            </section>

            {/* 12. Disclaimer */}
            <section className="terms-section">
              <h2>{terms.disclaimer_title}</h2>
              <p>{terms.disclaimer_text}</p>
            </section>

            {/* 13. Indemnification */}
            <section className="terms-section">
              <h2>{terms.indemnification_title}</h2>
              <p>{terms.indemnification_text}</p>
            </section>

            {/* 14. Account Termination */}
            <section className="terms-section">
              <h2>{terms.termination_title}</h2>
              <p>{terms.termination_text1}</p>
              {terms.termination_text2 && <p>{terms.termination_text2}</p>}
            </section>

            {/* 15. Modifications */}
            <section className="terms-section">
              <h2>{terms.modifications_title}</h2>
              <p>{terms.modifications_text}</p>
            </section>

            {/* 16. Governing Law */}
            <section className="terms-section">
              <h2>{terms.law_title}</h2>
              <p>{terms.law_text}</p>
            </section>

            {/* 17. Dispute Resolution */}
            <section className="terms-section">
              <h2>{terms.dispute_title}</h2>
              <p>{terms.dispute_text}</p>
            </section>

            {/* 18. Severability */}
            <section className="terms-section">
              <h2>{terms.severability_title}</h2>
              <p>{terms.severability_text}</p>
            </section>

            {/* 19. Entire Agreement */}
            <section className="terms-section">
              <h2>{terms.entire_title}</h2>
              <p>{terms.entire_text}</p>
            </section>

            {/* 20. Contact Information */}
            <section className="terms-section">
              <h2>{terms.contact_title}</h2>
              <p>{terms.contact_text}</p>
              <div className="contact-info">
                {terms.contact_email && (
                  <p><strong>Email:</strong> {terms.contact_email}</p>
                )}
                {terms.contact_phone && (
                  <p><strong>Phone:</strong> {terms.contact_phone}</p>
                )}
                {terms.contact_address && (
                  <p><strong>Address:</strong> {terms.contact_address}</p>
                )}
              </div>
            </section>
          </div>

          {terms.footer_text && (
            <div className="terms-footer">
              <p>{terms.footer_text}</p>
            </div>
          )}
        </div>
      </div>

      <BottomFooter />
    </>
  );
};

export default TermsAndConditions;
